import { createStore } from 'redux';
import thunk from 'redux-thunk';
import { isEqual, debounce, omit } from 'lodash';
import { search } from './actions/results';

import { applyMiddleware } from 'helpers/redux_dev_tools';
import rootReducer from './root_reducer';

import { fetchZonesList } from './actions/ui';
import { getDetailsOfPlace } from '../search/helpers/map_helpers';

type QueryType = {
  placeId?: string;
}

export default function (state, query: QueryType = {}) {
  const store = createStore(rootReducer, state, applyMiddleware(thunk));

  const shortDebounceSearch = debounce(() => {
    store.dispatch<any>(search());
  }, 500);

  const debouncedZoneFetch = debounce(() => {
    store.dispatch<any>(fetchZonesList());
  }, 500);

  let prevState = store.getState();

  store.subscribe(() => {
    const state = store.getState();
    if (!state.ui.mapLoaded || (state.ui.mapZoomReadonly || 0) <= 11 || state.ui.staticCallList) return;

    const hasBoundsChanged = !isEqual(prevState.criteria.mapBounds, state.criteria.mapBounds) &&
      state.criteria.mapBounds;
    if (hasBoundsChanged) debouncedZoneFetch();
  });

  store.subscribe(() => {
    const state = store.getState();
    const mapLoaded = !prevState.ui.mapLoaded && state.ui.mapLoaded;
    if (state.ui.staticCallList) return;

    if (mapLoaded && query.placeId) {
      getDetailsOfPlace({ placeId: query.placeId }).then((place) => {
        const detail = {
          placeId: query.placeId,
          address: place.formatted_address,
          types: place.types
        };

        const event = new CustomEvent('placeSelect', { detail });
        window.dispatchEvent(event);
      });
    } else if (
      (
        // Perform a search automatically on map load when the ui is in the list state
        mapLoaded && !state.ui.showMap
      ) ||
      (
        // Perform a search if any of the criteria aside from mapBounds changes
        !isEqual(omit(prevState.criteria, 'mapBounds'), omit(state.criteria, 'mapBounds'))
      ) ||
      (
        // Perform a search when changing between "All Properties" and "Exclusive Listings"
        prevState.ui.offMarketSearch !== state.ui.offMarketSearch
      ) ||
      (
        // Perform a search when toggling on the search on map move option
        prevState.ui.updateResultsOnMapMove === false && state.ui.updateResultsOnMapMove === true
      ) ||
      (
        // Perform a search when the map moves AND
        //   updateResultsOnMapMove is true OR the map is not showing the table view
        !isEqual(prevState.criteria.mapBounds, state.criteria.mapBounds) &&
          (state.ui.updateResultsOnMapMove || !state.ui.showTableView)
      )
    ) {
      shortDebounceSearch();
    }

    prevState = state;
  });

  if (module.hot) {
    module.hot.accept('./root_reducer', () => {
      store.replaceReducer(require('./root_reducer'));
    });
  }

  return store;
}
